import { supabase, cloudConfigured } from "./supabase";

// Per-user app data ↔ Supabase `user_kv`.
// localStorage stays the synchronous cache the whole app reads from; this module
// keeps it in sync with the cloud:
//   • login  → hydrate(): pull the user's rows into localStorage (local edits that
//              never reached the cloud win and are pushed instead)
//   • write  → markDirty(): debounced upsert; dirty keys are persisted so an edit
//              made offline (or right before closing the tab) is never lost or
//              overwritten by an older cloud copy.

// Which per-user keys live in the cloud. Practice records have their own table.
export const isCloudKey = (key: string) =>
  key === "state" || key.startsWith("drawings.") || key === "replay.sessions" || key === "analysis.db" || key === "practice.prefs";

interface Who {
  id: string;
  username: string;
}
let who: Who | null = null;
let timer = 0;
let pushing = false;

const lsKey = (username: string, key: string) => `mytv.u.${username}.${key}`;
const dirtyKey = (username: string) => `mytv.u.${username}.__cloudDirty`;

function loadDirty(username: string): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(dirtyKey(username)) || "[]"));
  } catch {
    return new Set();
  }
}
function saveDirty(username: string, s: Set<string>) {
  try {
    localStorage.setItem(dirtyKey(username), JSON.stringify([...s]));
  } catch {}
}

export function setCloudUser(u: Who | null) {
  who = u;
}

// Pull this user's data down (cloud wins, except keys with unsent local edits).
export async function hydrate(u: Who): Promise<void> {
  who = u;
  if (!cloudConfigured) return;
  const { data, error } = await supabase.from("user_kv").select("key, value");
  if (error) {
    console.warn("[cloud] hydrate failed", error.message);
    return;
  }
  const dirty = loadDirty(u.username);
  const cloudKeys = new Set<string>();
  for (const row of data ?? []) {
    cloudKeys.add(row.key);
    if (dirty.has(row.key)) continue; // local edit not yet uploaded — keep it
    try {
      localStorage.setItem(lsKey(u.username, row.key), row.value);
    } catch {}
  }
  // local data the cloud has never seen (made before the cloud existed) → upload
  const prefix = `mytv.u.${u.username}.`;
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith(prefix)) continue;
    const key = k.slice(prefix.length);
    if (isCloudKey(key) && !cloudKeys.has(key)) dirty.add(key);
  }
  saveDirty(u.username, dirty);
  if (dirty.size) await pushNow();
}

export function markDirty(key: string) {
  if (!who || !cloudConfigured || !isCloudKey(key)) return;
  const d = loadDirty(who.username);
  d.add(key);
  saveDirty(who.username, d);
  clearTimeout(timer);
  timer = window.setTimeout(() => void pushNow(), 700);
}

// Upload every dirty key (value = current localStorage, or a delete if it's gone).
export async function pushNow(): Promise<boolean> {
  if (!who || !cloudConfigured || pushing) return false;
  pushing = true;
  const u = who;
  try {
    const dirty = loadDirty(u.username);
    if (!dirty.size) return true;
    const upserts: { user_id: string; key: string; value: string; updated_at: string }[] = [];
    const deletes: string[] = [];
    for (const key of dirty) {
      const v = localStorage.getItem(lsKey(u.username, key));
      if (v == null) deletes.push(key);
      else upserts.push({ user_id: u.id, key, value: v, updated_at: new Date().toISOString() });
    }
    if (upserts.length) {
      const { error } = await supabase.from("user_kv").upsert(upserts, { onConflict: "user_id,key" });
      if (error) throw error;
    }
    if (deletes.length) {
      const { error } = await supabase.from("user_kv").delete().eq("user_id", u.id).in("key", deletes);
      if (error) throw error;
    }
    // clear only what we sent (new edits may have landed meanwhile)
    const now = loadDirty(u.username);
    for (const k of [...upserts.map((x) => x.key), ...deletes]) now.delete(k);
    saveDirty(u.username, now);
    return true;
  } catch (e) {
    console.warn("[cloud] push failed — will retry", (e as Error).message);
    clearTimeout(timer);
    timer = window.setTimeout(() => void pushNow(), 10_000);
    return false;
  } finally {
    pushing = false;
  }
}

// best-effort flush when the tab is hidden/closed
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void pushNow();
  });
}
